import { motion } from 'framer-motion';
import { MODULE_LABELS, TIER_COLOR } from '@/types/profiler';
import type { CheckResult } from '@/types/profiler';
import type { DossierSectionProps } from './types';

// ── helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function durationMs(start?: string, end?: string): string {
  if (!start || !end) return '—';
  try {
    const ms = new Date(end).getTime() - new Date(start).getTime();
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.round(ms / 60_000)}m`;
  } catch {
    return '—';
  }
}

// ── outcome chip ──────────────────────────────────────────────────────────────

function OutcomeChip({ result }: { result: CheckResult }) {
  const hit = result.hit_found;
  const status = result.status ?? 'COMPLETE';
  const color =
    hit ? '#FF1F1F'
      : status === 'DEGRADED' ? '#F0B429'
      : status === 'FAILED'   ? '#FF1F1F'
      : '#00C853';
  const label =
    hit         ? 'HIT'
      : status === 'DEGRADED' ? 'DEGRADED'
      : status === 'FAILED'   ? 'FAILED'
      : 'CLEAR';

  return (
    <span
      style={{
        background: `${color}15`,
        color,
        border: `1px solid ${color}30`,
        fontSize: 8,
        fontFamily: 'monospace',
        fontWeight: 700,
        padding: '2px 7px',
        borderRadius: 99,
      }}
    >
      {label}
    </span>
  );
}

// ── risk level chip ───────────────────────────────────────────────────────────

function RiskLevelChip({ result }: { result: CheckResult }) {
  const hit = result.hit_found;
  const color = hit ? '#FF1F1F' : '#5A7A9A';
  return (
    <span style={{ color, fontSize: 9, fontFamily: 'monospace', fontWeight: hit ? 700 : 400 }}>
      {hit ? 'ELEVATED' : 'NOMINAL'}
    </span>
  );
}

// ── main component ─────────────────────────────────────────────────────────────

export function IntelSummary({ report }: DossierSectionProps) {
  const checks: CheckResult[] = report.check_results ?? [];
  const tier = report.risk_tier;
  const tierColor = TIER_COLOR[tier];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25 }}
      className="flex flex-col gap-5"
    >
      {/* FATF compliance card */}
      <div
        style={{
          background: '#060E1A',
          border: '1px solid #1E2B3F',
          borderRadius: 8,
          padding: '16px 18px',
        }}
      >
        <p
          style={{
            color: '#5A7A9A',
            fontSize: 9,
            fontFamily: 'monospace',
            letterSpacing: '0.15em',
            marginBottom: 10,
          }}
        >
          FATF RECOMMENDATION 10 — COMPLIANCE STATEMENT
        </p>
        <p
          style={{
            color: '#5A7A9A',
            fontSize: 10,
            lineHeight: 1.7,
          }}
        >
          This due diligence report has been prepared in accordance with the following regulatory frameworks:
        </p>
        <ul style={{ color: '#5A7A9A', fontSize: 10, lineHeight: 1.9, paddingLeft: 16, marginTop: 6 }}>
          <li>
            <strong style={{ color: '#DCE8F5' }}>FATF Recommendation 10</strong> — Customer Due Diligence.
            Identification and verification of the customer&apos;s identity using reliable, independent source
            documents, data, or information.
          </li>
          <li>
            <strong style={{ color: '#DCE8F5' }}>RBI KYC Master Direction 2016</strong> (as amended) —
            Reserve Bank of India directions on Know Your Customer standards for regulated entities,
            including Section 38 (EDD for High-Risk Customers) and Section 56 (PEP screening obligations).
          </li>
          <li>
            <strong style={{ color: '#DCE8F5' }}>PMLA Section 12</strong> — Maintenance of records of
            identity of clients under the Prevention of Money Laundering Act, 2002. Records must be
            maintained for a minimum of five years from the date of cessation of transactions.
          </li>
          <li>
            <strong style={{ color: '#DCE8F5' }}>UNODC / UN Security Council Resolutions</strong> —
            Consolidated sanctions screening against UN-designated entities and individuals.
          </li>
        </ul>
        <p style={{ color: '#283A50', fontSize: 9, fontFamily: 'monospace', marginTop: 10 }}>
          This report is algorithmically generated and must be reviewed by a qualified compliance officer
          before any regulatory determination. Classification: TLP:AMBER.
        </p>
      </div>

      {/* Module findings table */}
      {checks.length > 0 && (
        <div>
          <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.15em', marginBottom: 10 }}>
            MODULE FINDINGS SUMMARY
          </p>
          <div
            style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 8, overflow: 'hidden' }}
          >
            {/* Header */}
            <div
              className="grid gap-2 px-4 py-2"
              style={{
                gridTemplateColumns: '120px 110px 80px 1fr 80px',
                background: '#090D13',
                borderBottom: '1px solid #182030',
              }}
            >
              {['MODULE', 'SOURCE', 'OUTCOME', 'DETAIL', 'RISK LEVEL'].map(h => (
                <span key={h} style={{ color: '#283A50', fontSize: 8, fontFamily: 'monospace', letterSpacing: '0.1em' }}>
                  {h}
                </span>
              ))}
            </div>
            {checks.map((cr, i) => (
              <div
                key={i}
                className="grid gap-2 px-4 py-2.5 items-center"
                style={{
                  gridTemplateColumns: '120px 110px 80px 1fr 80px',
                  borderBottom: i < checks.length - 1 ? '1px solid #0F1928' : undefined,
                  borderLeft: cr.hit_found ? '3px solid #FF1F1F' : '3px solid transparent',
                }}
              >
                <span style={{ color: '#DCE8F5', fontSize: 10, fontWeight: 600 }}>
                  {MODULE_LABELS[cr.module] ?? cr.module}
                </span>
                <span style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace' }}>
                  {cr.source ?? '—'}
                </span>
                <OutcomeChip result={cr} />
                <span style={{ color: '#5A7A9A', fontSize: 9, lineHeight: 1.4 }}>
                  {cr.result_summary ?? '—'}
                </span>
                <RiskLevelChip result={cr} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Audit trail */}
      <div>
        <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.15em', marginBottom: 10 }}>
          CASE AUDIT TRAIL
        </p>
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: 'CASE REFERENCE', value: report.case_reference, mono: true },
            { label: 'GENERATED AT', value: formatDate(report.completed_at ?? report.created_at) },
            { label: 'CASE TYPE', value: report.case_type ?? 'CDD' },
            {
              label: 'RISK TIER',
              value: tier,
              color: tierColor,
            },
            {
              label: 'PIPELINE DURATION',
              value: durationMs(report.created_at, report.completed_at),
              mono: true,
            },
            { label: 'TLP CLASSIFICATION', value: 'TLP:AMBER', color: '#F0B429' },
            { label: 'REGULATORY BASIS', value: 'FATF Rec.10 / RBI KYC / PMLA S.12' },
            { label: 'MODULES RUN', value: String(checks.length), mono: true },
          ].map(item => (
            <div
              key={item.label}
              style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 8, padding: '10px 12px' }}
            >
              <div style={{ color: '#283A50', fontSize: 8, fontFamily: 'monospace', letterSpacing: '0.1em', marginBottom: 4 }}>
                {item.label}
              </div>
              <div
                style={{
                  color: item.color ?? '#DCE8F5',
                  fontSize: 11,
                  fontFamily: (item as { mono?: boolean }).mono ? 'monospace' : undefined,
                  fontWeight: 600,
                  wordBreak: 'break-all',
                }}
              >
                {item.value ?? '—'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Degraded modules */}
      {(report.degraded_modules?.length ?? 0) > 0 && (
        <div
          style={{ background: '#1A1000', border: '1px solid #F0B42920', borderRadius: 8, padding: '10px 14px' }}
        >
          <p style={{ color: '#F0B429', fontSize: 9, fontFamily: 'monospace', marginBottom: 6 }}>
            DEGRADED MODULES — results may be incomplete
          </p>
          <div className="flex flex-wrap gap-1.5">
            {report.degraded_modules!.map(m => (
              <span
                key={m}
                style={{
                  background: '#F0B42912',
                  color: '#F0B429',
                  border: '1px solid #F0B42920',
                  fontSize: 8,
                  fontFamily: 'monospace',
                  padding: '2px 7px',
                  borderRadius: 99,
                }}
              >
                {MODULE_LABELS[m] ?? m}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* TLP:AMBER classification footer */}
      <div
        style={{
          background: '#1A1000',
          border: '1px solid #F0B42940',
          borderRadius: 8,
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span style={{ color: '#F0B429', fontSize: 9, fontFamily: 'monospace', lineHeight: 1.5 }}>
          TLP:AMBER — Recipients may only share this report with members of their organisation
          and with third parties who need to know in order to protect themselves or prevent further harm.
          Sources are not to be identified.
        </span>
        <span
          style={{
            background: '#F0B42925',
            color: '#F0B429',
            border: '1px solid #F0B42950',
            fontSize: 9,
            fontFamily: 'monospace',
            fontWeight: 800,
            padding: '4px 12px',
            borderRadius: 6,
            flexShrink: 0,
            marginLeft: 12,
            letterSpacing: '0.1em',
          }}
        >
          TLP:AMBER
        </span>
      </div>
    </motion.div>
  );
}
